import React from 'react';
import Head from 'next/head';
import {GetServerSideProps} from 'next';
import {QueryClient} from '@tanstack/react-query';
import PanelistaHeaderLink from 'src/components/PanelistaHeaderLink';
import {DocumentStyles} from 'src/pages/legal/docStyles';
import ssrTranslations from 'src/utils/ssrTranslations';
import PageContent from 'src/components/PageContent';
import {nsTranslation} from 'src/services/i18n';
import Header from 'src/components/Header';
import getTitle from 'src/utils/getTitle';
import Page from 'src/components/Page';

const PanelistaTermsOfService = () => {
  return (
    <Page>
      <Header CenterComponent={<PanelistaHeaderLink />} />
      <PageContent style={{marginTop: 40}}>
        <Head>
          <title>{getTitle('Terms of Service')}</title>
        </Head>
        <DocumentStyles>
          <h1>Terms of Service</h1>
          <h2>1. GENERAL</h2>
          <p>
            1.1
            <br />
            Output Bypropel AB (“We” or “Propel”), who has built Panelista, is on a mission to make it easier for people
            to help improve the companies they care about, and for companies to collaborate with their target groups.
            These terms of service (the “Terms”) describe our commitments to you, and your rights and responsibilities
            when using our services. Please read them carefully and reach out to us if you have any questions.
          </p>

          <p>
            1.2
            <br />
            These Terms govern all access to and use of the website https://panelista.com, as well as all content,
            products and services available at or through the digital product Panelista (collectively, “the Services”),
            and always apply between Propel and all users of the Services (“You” or the “User”). These Terms also govern
            the User’s access to and use of any panels and touchpoints hosted through the Services that are operated by
            other Users.
          </p>

          <p>
            1.3
            <br />
            By using, creating an account and/or by logging onto the Services (whether directly or indirectly), You
            accept and agree to the Terms and to Propel’s Privacy Policy and Cookie Policy. Propel reserves the right
            to, without liability, at any time, amend, add or remove parts of these Terms, without first obtaining the
            User’s approval. It is Your own responsibility to stay informed of any update of the Terms each time You use
            the Service. Your continued use of the Service after changes have been made in the Terms means that You
            accept and agree to those changes. You understand and agree that as a User of the Services You remain bound
            by and shall adhere to the Terms even though You may be contractually licensed to use the Services under a
            separate confirmed order or license agreement by and between the legal entity You represent (“Your Company”)
            and Propel.
          </p>

          <h2>2. USER RESPONSIBILITIES AND WARRANTIES</h2>
          <p>
            2.1
            <br />
            The User undertakes to comply with the at all times applicable instructions, technical specifications and
            other regulations that Propel provides for the use of the Services.
          </p>

          <p>
            2.2
            <br />
            The User warrants that he/she will not use the Services for any purpose contrary to the Terms or to
            applicable law. In the event of abuse of the Services, the User shall compensate Propel for any damage that
            occurs in relation to the abuse. The User shall also indemnify, and hold Propel harmless for any damage that
            the User causes Propel by using the Services in violation of the Terms.{' '}
          </p>

          <p>
            2.3
            <br />
            The User is personally responsible for all documents and activities carried out by him or her within the
            scope of the Services. The User therefore warrants that the User’s use of the Services:
          </p>

          <ul>
            <li>Will not infringe or misappropriate the intellectual property rights of any third party;</li>
            <li>Will not overburden the systems of the Services, as determined by Propel at its sole discretion;</li>
            <li>Will not disclose sensitive personal information of others;</li>
            <li>Will not be used to send spam or bulk unsolicited messages;</li>
            <li>Will not interfere with, disrupt, or attack any service or network; and</li>
            <li>
              Will not be used to create, distribute, or enable material that is - or that facilitates or operates in
              conjunction with - malware, spyware, adware, or other malicious programs or code.
            </li>
          </ul>

          <h2>3. CONTENT</h2>

          <p>
            3.1
            <br />
            Propel has not reviewed, and cannot review, all of the content (such as, but not limited to, text, photo,
            video, audio, code, computer software, items for sale, or other materials) posted to the Services by Users
            or anyone else (“Content”) and is not responsible for any use or effects of such Content. For example:
          </p>

          <ul>
            <li>
              We do not endorse any Content or warrant that any Content is accurate, useful, or non-harmful. Content
              could be offensive, indecent or objectionable; include technical inaccuracies, typographical mistakes or
              other errors; or violate or infringe the privacy, publicity rights, intellectual property rights, or other
              proprietary rights of third parties.
            </li>
            <li>
              If You post Content or otherwise make (or allow any third party to make) Content available through the
              Services, You are entirely responsible for the Content, and any harm resulting from, that Content or Your
              conduct.
            </li>
            <li>
              We disclaim any responsibility for any harm resulting from anyone’s use, purchase, or downloading of
              Content. If You access or use any Content, You are responsible for taking precautions as necessary to
              protect Yourself and Your computer systems from viruses, worms, trojan horses, and other harmful or
              destructive content.
            </li>
          </ul>

          <p>
            3.2
            <br />
            Please note that additional third party terms and conditions may apply to the downloading, copying,
            purchase, or use of Content.
          </p>

          <p>
            3.3
            <br />
            We also have not reviewed, and cannot review, all of the material, including computer software, made
            available through the websites and web pages that link to, or are linked from, the Services. For example:
          </p>

          <ul>
            <li>
              We do not have any control over those websites and are not responsible for their contents or their use.
            </li>
            <li>
              The existence of a link to or from one of our Services does not represent or imply that we endorse such
              website.
            </li>
            <li>
              You are responsible for taking precautions as necessary to protect Yourself and Your computer systems from
              viruses, worms, trojan horses, and other harmful or destructive content.
            </li>
            <li>We disclaim any responsibility for any harm resulting from non-Propel websites.</li>
          </ul>

          <h2>4. SPECIFIC SERVICE TERMS</h2>
          <p></p>
          <h4>4.1 Panelista Panels, Touchpoints and Accounts</h4>
          <p>
            The Services enable Users to ask for and provide feedback. Users can invite people to a panel where the User
            is able to run activities with them as they become Users themselves. The User can also create external
            digital forms or “touchpoints” to be used in places outside Panelista, including but not limited to, by
            being embedded on other websites, and linked to from mobile apps, emails and QR-codes, where people can
            answer questions or supply other information without being a User themselves. Each User agrees that the User
            who creates a panel or a touchpoint accrues all rights to content posted on such panel or via such
            touchpoint, and that such User may freely dispose of the content in whichever way it wishes, without regard
            to copyright or any other right of the User who posted in the panel or the person who interacted via the
            touchpoint. Both the User who created the panel or touchpoint, and the one who publishes content are
            responsible for what’s published. All Users therefore agrees to make sure that no prohibited content (like
            spam, viruses, or serious threats of violence) appears in panels or via touchpoints.
          </p>

          <h4>4.2 Web Traffic.</h4>
          <p>
            Propel uses a third party analytics solution Plausible, to measure the Services’ audience and usage. By
            hosting Your panel or touchpoint through the Services, You agree to Propel doing so.
          </p>
          <h4>4.3 Attribution.</h4>
          <p>
            Propel reserves the right to display attribution text or links in Your panel or touchpoint footer or
            toolbar, attributing Panelista or Propel, for example. Some of these attributions may not be altered or
            removed.
          </p>
          <h4>4.4 Personal Data</h4>
          <p>
            Propel processes the User's personal data in accordance with regulation (EU) 2016/679 of the European
            parliament and of the council of 27 April 2016 on the protection of individuals with regard to the
            processing of personal data and on the free movement of such data and repealing directive 95/46/EC and the
            Swedish law (2018: 218) with supplementary provisions to the EU data protection regulation (jointly referred
            to as “GDPR”). For more information regarding Propel’s personal data processing, please refer to Propel’s
            privacy policy.
          </p>

          <h2>5. USER ACCOUNT</h2>
          <p>
            5.1
            <br />
            You are responsible for maintaining the confidentiality of, and protecting Your account information,
            including passwords. You are responsible for all activity on Your account. You shall immediately notify
            Propel on suspicion of unauthorized use or attempted unauthorized use of Your account or Your code, or other
            security breach and take measures to limit such third party’s access to the Services. You accept that Propel
            is not responsible for any unauthorized access to and abuse of the Services and for any consequences
            thereof, which are the result of Your negligence.
          </p>

          <p>
            5.2
            <br />
            You agree that You will provide accurate, current and complete information about Yourself within any account
            information registered in the Services. You shall keep all Your account information accurately updated by
            following the procedures provided by the Service. You may not use any automated device, program, algorithm,
            method, or any similar manual process, to access, acquire, copy, probe, load-test, index, manipulate, test
            or monitor any portion of the Service or any content, or to in any way reproduce or circumvent the Service’s
            structure or presentation, or to attempt to obtain any materials, documents or information through any means
            other than the infrastructure created and/or made available by Propel to that end. You agree that You will
            not perform any action that exposes the Service’s infrastructure or subject any systems or networks that are
            part of or affiliated with the Service, to an excessive burden.
          </p>

          <p>
            5.3
            <br />
            Restrictions for normal use apply as per the price plans published on Panelista, except in the event
            You/Your company has a separate valid license agreement with Propel – or with an authorized reseller of the
            Services (“Reseller”) – which includes price plans with other conditions about normal use.
          </p>

          <p>
            5.4
            <br />
            You agree that Propel at any time, without notice, may freeze Your account in the Service or otherwise stop
            Your access to the Service for (1) an alleged breach of the Terms, (2) our attempts to address safety in the
            software or to protect the contents, (3) a modification of the Service, (4) an unexpected operational
            interruptions or problems, (5) a delayed payment, (6) a malicious breach of any applicable limits of normal
            usage (max 500 panelists/panel, 30 questions/month/panel, 10 000 touchpoint feedback
            answers/month/touchpoint, 1 000 000 page loads/month/touchpoint widget, 20 panels + 100 touchpoints + 100
            administrators per project), (7) an explicit action requested from any legal authority or other government
            authorities. You agree that Propel also is entitled to, with a week’s notice, terminate Your account for (1)
            clear violations of the Terms, or (2) an explicit action request from any legal authority or other
            government authorities. Propel will not be liable to You or any third party due to Your account in the
            Service being frozen or terminated in accordance herewith.
          </p>

          <h2>6. CHANGES IN THE SERVICES AND SHUTDOWN</h2>
          <p>
            6.1
            <br />
            Propel is entitled at any time, without prior notice, to change the design of the Services and its
            functions, as well as how the Services are provided. Propel also has the right to implement updates,
            develop, improve and modify the Services to such an extent as Propel deems necessary or appropriate. Such
            changes may result in some of the Services' functions being changed, terminated or added. Propel also has
            the right to at any time shut down the Services permanently.
          </p>

          <p>
            To avoid misunderstandings, the above only applies to the Users of the Service. Propel’s Clients are
            referred to their respective agreements with Propel in regards to the provision of the Services. A Client is
            a company with an agreement with Propel for the right to use Panelista.
          </p>

          <h2>7. INTELLECTUAL PROPERTY RIGHTS</h2>
          <p>
            7.1
            <br />
            Propel is the sole owner of all rights, including intellectual property rights to the Services, including
            but not limited to rights concerning trademarks, trade names, design, text, software, etc., except for
            certain rights owned by Propel’s licensors. Nothing in the Terms shall be construed as if any of these
            rights being wholly or partly transferred to the User. Any copying, modification, transfer, licensing and/or
            other use of the Services other than allowed under the Terms is prohibited.
          </p>

          <p>
            7.2
            <br />
            The User understands that the unauthorized use of Propels intellectual property rights constitutes a breach
            of contract and may constitute a criminal action. Propel has the right to take legal action against the User
            in the event of such unauthorized use of Propel intellectual property rights.
          </p>

          <h2>8. COMMUNICATION</h2>
          <p>
            8.1
            <br />
            The User agrees that all communication between the parties, including but not limited to confirmations,
            reminders, terminations, notices and other information in connection with the Services and/or the Terms,
            shall be sent electronically by email between the parties or such other means as Propel specifies in the
            Services or otherwise.
          </p>
          <p>
            8.2
            <br />
            The User undertakes to state the current and valid email address to which the User wishes the communication
            be sent when registering the user account. The User further undertakes to immediately notify Propel of
            updated contact information.
          </p>

          <h2>9. LIMITATION OF LIABILITY</h2>
          <p>
            9.1
            <br />
            The Services are provided “as is”. Propel and its suppliers and licensors hereby disclaim all warranties of
            any kind, expressed or implied, including, without limitation, the warranties of merchantability, fitness
            for a particular purpose and non-infringement. Neither Propel, nor its suppliers and licensors, make any
            warranty that our Services will be error free or that access thereto will be continuous or uninterrupted.
            You understand that You download from, or otherwise obtain content or services through, our Services at Your
            own discretion and risk.
          </p>
          <p>
            9.2
            <br />
            Propel is not responsible for the communication that takes place between Users, for information that the
            User documents or the User's actions or conduct in general. Propel is not responsible for interruptions or
            errors caused by third party systems and cannot be held liable for damages that are caused by a loss of
            information for the User.
          </p>
          <p>
            9.3
            <br />
            The User's right to compensation for damages according to the Terms shall under all circumstances be limited
            to direct damages and to SEK 1 000. The limitation of liability also applies in relation to the User's
            possible liability towards third parties. The User's request for compensation in accordance with the Terms
            must be made to Propel within a reasonable time from the time the damage was discovered or should have been
            discovered (normally within 30 days). The User is not entitled to any other remedies or remunerations than
            those stated in these Terms.
          </p>
          <p>
            9.4
            <br />
            With the exception of events where Propel or the User has acted grossly negligently or intentionally, such
            party shall be exempt from liability for breach of the Terms and/or sanction as a result of failure to
            fulfill obligations under the Terms due to circumstance that hinder or significantly impede or delay
            performance. Such a circumstance is, for example, war, terrorist attack, authority regulation, action or
            omission, labor market conflict (even where the breaching party does not itself participate in the
            conflict), blockade, fire, restrictions on energy supply, flood or other natural disaster or accident of
            greater magnitude or other circumstances outside of the breaching party’s control, which the breaching party
            reasonably could not have foreseen and the consequences of which the breaching party could not have
            reasonably avoided or overcome. A party wishing to invoke such a force majeure condition shall without delay
            inform the counterparty in writing of the occurrence or cessation of the force majeure condition.
          </p>

          <h2>10. ASSIGNMENT</h2>

          <p>
            10.1
            <br />
            The User is not entitled to transfer all or part of his or her rights or obligations under the Terms to
            another party. However, Propel always has the right to assign its rights and obligations to another company.
          </p>

          <h2>11. APPLICABLE LAW</h2>

          <p>
            11.1
            <br />
            Swedish law shall apply to the Terms.
          </p>

          <p>
            11.2
            <br />
            Disputes arising in connection with the Terms shall, as a starting point, be resolved by agreement. If the
            parties cannot reach such an agreement within a reasonable time, the dispute shall be settled in general
            court, with the Gothenburg District Court (Sw: Göteborgs tingsrätt) as the first instance.
          </p>

          <h2>12. UPDATES AND CONTACT INFORMATION</h2>

          <p>
            12.1
            <br />
            We make continuous changes to these Terms of Service. A new version becomes applicable when made available
            here on our website.
          </p>

          <p>
            12.2
            <br />
            Last updated: 2021-06-14.
          </p>
        </DocumentStyles>
      </PageContent>
    </Page>
  );
};

PanelistaTermsOfService.isPublicPage = true;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const queryClient = new QueryClient();

  return {props: {...(await ssrTranslations(context, queryClient, [nsTranslation]))}};
};

export default PanelistaTermsOfService;
